import { createContext, ReactNode, useContext, useState, useMemo } from "react";

export interface SearchContextType {
  claimReference: string;
  firstName: string;
  middleNames: string;
  lastName: string;
  postcode: string;
  emailAddress: string;
  nationalInsuranceNumber: string;
  drivingLicenseNumber: string;
  registrationNumber: string;
  passportNumber: string;
  vinNo: string;
  telNo: string;
  mobileTelNo: string;
  incidentDate: Date | null;
  dateOfBirth: Date | null;
  fraudDtectionConsent: boolean;
  fullAddress: string;
  pafudprn: string;
  organisationType?: string | null;
  organisationPostCode?: string | null;
  organisationName?: string | null;
  organisationHouseNumber?: string | null;
  organisationGmcNumber?: string | null;
  optionalSearchValues: string[];
  updateFormData: (data: Partial<SearchContextType>) => void;
  resetFormData: () => void;
}

export const optionalKeys = [
  "emailAddress",
  "nationalInsuranceNumber",
  "drivingLicenseNumber",
  "registrationNumber",
  "passportNumber",
  "vinNo",
  "telNo",
  "mobileTelNo",
  "fullAddress",
  "organisationName",
];

const initialFormData: Omit<
  SearchContextType,
  "updateFormData" | "optionalSearchValues" | "resetFormData"
> = {
  claimReference: "",
  firstName: "",
  middleNames: "",
  lastName: "",
  postcode: "",
  emailAddress: "",
  nationalInsuranceNumber: "",
  drivingLicenseNumber: "",
  registrationNumber: "",
  passportNumber: "",
  vinNo: "",
  telNo: "",
  mobileTelNo: "",
  incidentDate: null,
  dateOfBirth: null,
  fullAddress: "",
  pafudprn: "",
  organisationType: null,
  organisationPostCode: null,
  organisationName: null,
  organisationHouseNumber: null,
  organisationGmcNumber: null,
  fraudDtectionConsent: false,
};

export type OptionalKeys = (typeof optionalKeys)[number];

const SearchContext = createContext<SearchContextType | undefined>(undefined);

interface SearchProviderProps {
  children: ReactNode;
}

const SearchProvider = ({ children }: SearchProviderProps) => {
  const [formData, setFormData] =
    useState<
      Omit<
        SearchContextType,
        "updateFormData" | "resetFormData" | "optionalSearchValues"
      >
    >(initialFormData);

  const updateFormData = (data: Partial<SearchContextType>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const resetFormData = () => {
    setFormData(initialFormData);
  };

  const value = useMemo(
    () => ({
      ...formData,
      optionalSearchValues: optionalKeys,
      updateFormData,
      resetFormData,
    }),
    [formData],
  );

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
};

const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
};

export { SearchProvider, useSearch };
